"use client";

/**
 * Show prices in the visitor's currency of choice, at today's exchange rate
 * (Gordon 2026-11-01: "a currency selector with up to date exchange to the
 * launch page and why-bb page"). Starts from the visitor's own currency
 * (geo cookie / locale, see useCurrency), remembers a pick per device, and
 * names the rate source + date so the numbers can be checked.
 */
import { ChevronDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useCurrency } from "@/hooks/use-currency";
import { cn } from "@/lib/utils";

const STORE_KEY = "bb-display-currency";
/** Offered first; the visitor's own currency is added when it isn't here. */
const COMMON = ["EUR", "USD", "GBP", "SEK", "NOK", "DKK", "ISK", "CHF", "PLN", "CZK", "HUF", "CAD", "AUD", "NZD", "JPY", "KRW", "CNY", "INR", "BRL", "MXN", "ZAR", "NGN", "KES", "GHS", "AED", "SAR", "TRY"];

/** Where /api/fx says the rates come from → a name and a page to check them. */
const SOURCES: Record<string, { label: string; url: string }> = {
  "open.er-api.com": { label: "ExchangeRate-API", url: "https://www.exchangerate-api.com/docs/free" },
};

export interface DisplayCurrency {
  code: string;
  setCode: (code: string) => void;
  /** Units of `code` per 1 USD. */
  perUsd: number;
  /** Units of `code` per 1 EUR. */
  perEur: number;
  /** Units of `code` per Swedish krona (fallback ≈ 11 SEK/EUR when the rate is missing). */
  perSek: number;
  /** Formats an amount already in `code`. */
  money: (amount: number) => string;
  options: string[];
  updated: number | null;
  source: { label: string; url: string } | null;
}

export function useDisplayCurrency(): DisplayCurrency {
  const locale = useLocale();
  const { target, rates, updated, source } = useCurrency();
  const [picked, setPicked] = useState<string | null>(null);
  useEffect(() => {
    try { const s = localStorage.getItem(STORE_KEY); if (s) setPicked(s); } catch { /* private mode */ }
  }, []);
  const code = picked && rates[picked] ? picked : rates[target] ? target : "EUR";
  const setCode = (c: string) => {
    setPicked(c);
    try { localStorage.setItem(STORE_KEY, c); } catch { /* private mode */ }
  };
  const options = useMemo(() => (COMMON.includes(target) || !rates[target] ? COMMON : [target, ...COMMON]).filter(c => rates[c]), [target, rates]);
  const fmt = useMemo(() => new Intl.NumberFormat(locale, { style: "currency", currency: code }), [locale, code]);
  return {
    code, setCode, options, updated,
    perUsd: rates[code] ?? 1,
    perEur: (rates[code] ?? 1) / (rates.EUR || 1),
    perSek: rates.SEK ? (rates[code] ?? 1) / rates.SEK : ((rates[code] ?? 1) / (rates.EUR || 1)) / 11,
    money: (amount: number) => fmt.format(amount),
    source: source && source !== "fallback" ? SOURCES[source] ?? { label: source, url: `https://${source}` } : null,
  };
}

export function CurrencySelect({ value, onChange, options, className }: { value: string; onChange: (code: string) => void; options: string[]; className?: string }) {
  const t = useTranslations("common");
  const locale = useLocale();
  const names = useMemo(() => { try { return new Intl.DisplayNames([locale], { type: "currency" }); } catch { return null; } }, [locale]);
  return (
    <label className={cn("inline-flex max-w-full items-center gap-2 text-sm font-semibold", className)}>
      <span className="shrink-0">{t("currency")}</span>
      {/* The button is just wider than the chosen currency (Gordon 2026-11-01:
          a native select is as wide as its LONGEST option and stuck out of the
          page). The real select lies invisible on top, so a tap still opens
          the device's own picker. */}
      <span className="relative inline-flex h-11 min-w-0 items-center gap-1.5 rounded-md border-2 border-current px-3 focus-within:ring-2 focus-within:ring-ring">
        <span className="truncate">{value} · {names?.of(value) ?? value}</span>
        <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          aria-label={t("currency")}
          className="absolute inset-0 h-full w-full cursor-pointer appearance-none opacity-0"
        >
          {options.map(c => <option key={c} value={c} className="bg-background text-foreground">{c} · {names?.of(c) ?? c}</option>)}
        </select>
      </span>
    </label>
  );
}

/** A 1-2-5 step that gives about `n` notches up to `max`. */
export function niceStep(max: number, n = 30): number {
  const raw = max / n;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const m = raw / pow;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * pow;
}

/** Rounds `v` to the nearest multiple of `step`, never below one step. */
export const snap = (v: number, step: number) => Math.max(step, Math.round(v / step) * step);
